import { accountBadgeEntityName, canAccountBadgeBeReissued } from '../../../../backend/features/badges/shared';
import { NoAccountForBulkActionDialogContent } from '../../../../ui/features/badge-management/components/NoAccountForBulkActionDialogContent';
import { AccountBadgeContext } from '../../../../ui/features/badge-management/contexts/AccountBadgeContext';
import { AccountContext } from '../../../../ui/features/badge-management/contexts/AccountContext';
import { useBadgingTranslation } from '../../../../ui/hooks';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { accountEntityName } from '@juvare/contact-management/backend/features/accounts/shared';
import { useStorageList } from '@juvare/platform-sdk-react/ui/hooks';
import { Alert, Box, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import React from 'react';
import { AccountsSelectedTable } from './BadgeAccountsSelectionPanel/AccountsSelectedTable';
export const AccountBadgeReissueBulkActionDialog = ({ bulkActionFilters, onSubmit, onCancel, }) => {
    const { t } = useBadgingTranslation();
    const { adjustAccountSelectionChanged, extendAccountSelectionSelectedColumns } = React.useContext(AccountContext);
    const { mapAccountBadgeColumnsToSelectionOptions } = React.useContext(AccountBadgeContext);
    const [accountSelections, setAccountSelections] = React.useState([]);
    const { data: accountBadges, isLoading: isAccountBadgesLoading } = useStorageList(accountBadgeEntityName, {
        filter: bulkActionFilters,
    });
    const accountBadgesToReissue = React.useMemo(() => accountBadges === null || accountBadges === void 0 ? void 0 : accountBadges.rows.filter((ab) => canAccountBadgeBeReissued(ab).canReissue), [accountBadges]);
    const { data: accounts, isLoading: isAccountsLoading } = useStorageList(accountEntityName, {
        filter: [{ id: (accountBadgesToReissue !== null && accountBadgesToReissue !== void 0 ? accountBadgesToReissue : []).map((ab) => ab.columns.accountId), op: 'in' }],
    });
    React.useEffect(() => {
        if (!(accountBadgesToReissue === null || accountBadgesToReissue === void 0 ? void 0 : accountBadgesToReissue.length) || !(accounts === null || accounts === void 0 ? void 0 : accounts.rows.length)) {
            return;
        }
        const accountSelectionsFromOutside = () => accountBadgesToReissue.flatMap((ab) => {
            var _a;
            const account = accounts.rows.find((acc) => acc.id === ab.columns.accountId);
            return account
                ? {
                    id: ab.columns.accountId,
                    accountBadgeId: ab.id,
                    account: account.columns,
                    options: (_a = mapAccountBadgeColumnsToSelectionOptions === null || mapAccountBadgeColumnsToSelectionOptions === void 0 ? void 0 : mapAccountBadgeColumnsToSelectionOptions(ab.columns)) !== null && _a !== void 0 ? _a : {
                        organizationId: ab.columns.organizationId,
                    },
                }
                : [];
        });
        setAccountSelections(accountSelectionsFromOutside);
    }, [accountBadgesToReissue, accounts === null || accounts === void 0 ? void 0 : accounts.rows, mapAccountBadgeColumnsToSelectionOptions]);
    const handleChangeAccountSelectionOptions = React.useCallback((accountId, optionsPartialChanges) => {
        setAccountSelections((accountSelectionsCurrent) => {
            return accountSelectionsCurrent.map((selection) => selection.id === accountId
                ? Object.assign(Object.assign({}, adjustAccountSelectionChanged(Object.assign(Object.assign({}, selection), { options: Object.assign(Object.assign({}, selection.options), optionsPartialChanges) }))), { accountBadgeId: selection.accountBadgeId })
                : selection);
        });
    }, [adjustAccountSelectionChanged, setAccountSelections]);
    const handleRemoveAccountBadges = (idsToRemove) => {
        setAccountSelections((accounts) => accounts.filter((a) => !idsToRemove.includes(a.id)));
    };
    const handleSubmit = () => {
        onSubmit(accountSelections);
    };
    if (isAccountsLoading || isAccountBadgesLoading) {
        return (React.createElement(DialogContent, null,
            React.createElement(Box, { sx: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                } },
                React.createElement(CircularProgress, null))));
    }
    if (!(accountBadgesToReissue === null || accountBadgesToReissue === void 0 ? void 0 : accountBadgesToReissue.length)) {
        return (React.createElement(NoAccountForBulkActionDialogContent, { bulkActionTitle: t('badges.badgeDetails.accountBadgeTable.reissueBulkActionDialog.noEligibleTitle'), onClose: onCancel }));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(DialogTitle, null, t('badges.badgeDetails.accountBadgeTable.reissueBulkActionDialog.title', {
            count: accountBadgesToReissue.length,
        })),
        React.createElement(DialogContent, null,
            React.createElement(Typography, { sx: { mb: 2 } }, t('badges.badgeDetails.accountBadgeTable.reissueBulkActionDialog.subtitle')),
            React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: themeColors.surface.disable,
                    p: 2,
                    gap: 2,
                } },
                React.createElement(Alert, { severity: 'info' }, t('badges.badgeDetails.accountBadgeTable.reissueBulkActionDialog.notice')),
                React.createElement(AccountsSelectedTable, { selectedAccounts: accountSelections, renderColumns: extendAccountSelectionSelectedColumns === null || extendAccountSelectionSelectedColumns === void 0 ? void 0 : extendAccountSelectionSelectedColumns(setAccountSelections), onAddAccountClick: undefined, onUnselectAccounts: handleRemoveAccountBadges, onSetSelectedAccountOptions: handleChangeAccountSelectionOptions }))),
        React.createElement(DialogActions, null,
            React.createElement(Button, { color: 'primary', "data-test": 'form-dialog-submit-button', onClick: onCancel }, t('common.cancel')),
            React.createElement(Button, { color: 'primary', "data-test": 'form-dialog-submit-button', onClick: handleSubmit }, t('badges.badgeDetails.accountBadgeTable.reissueBulkActionDialog.submit')))));
};
